import { Star } from 'lucide-react';
import { formatCurrencyFr } from '@/lib/formatters';
import MarkdownText from '../MarkdownText';
import { formatPeriod } from '../utils';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function formatPeriodMonth(period) {
  if (!period) return '';
  const m = parseInt(String(period).split('-')[1], 10) - 1;
  return MONTHS_FR[m] || '';
}

function formatStepDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' });
  } catch {
    return dateStr;
  }
}

function pad2(n) { return String(n).padStart(2, '0'); }

/**
 * Carte d'une priorité du Plan d'action — 1 sur 3.
 *
 * Layout (brief produit) :
 *   - Pavé numéroté "PRIORITÉ 01" en serif sur fond ivoire foncé (top bar)
 *   - Étoile dorée à côté du numéro si satellite_signal_id présent
 *   - Titre H3 16px
 *   - Encadré "Question de contexte" en fond crème, italique
 *   - Paragraphe d'analyse senior chiffrée (markdown)
 *   - Encadré "Plan d'action concret · [mois]" : 5 étapes datées avec bullets bordeaux
 *   - Footer 3 indicateurs : impact € annualisé / difficulté / délai
 */
export default function PriorityCard({ priority, rank, report }) {
  const monthLabel = formatPeriodMonth(report?.period);
  const periodReadable = formatPeriod(report?.period);

  const satelliteId = priority?.satellite_signal_id;
  const satelliteSignal = satelliteId
    ? (report?.satellite_signals_selected || []).find((s) => s.signal_id === satelliteId)
    : null;
  const showStar = Boolean(satelliteSignal);

  const steps = Array.isArray(priority?.action_steps) ? priority.action_steps : [];
  const analysis = priority?.analysis_md || priority?.analysis || '';

  return (
    <article
      className="bg-cabinet-paper border border-cabinet-border shadow-sm"
      data-testid={`priority-card-${rank}`}
    >
      {/* Top bar : pavé numéroté + étoile éventuelle */}
      <header className="bg-cabinet-cream-dark border-b border-cabinet-border px-7 py-3 flex items-center justify-between gap-3">
        <div className="font-playfair text-[12px] tracking-[0.32em] uppercase text-cabinet-bordeaux">
          Priorité {pad2(rank)}
        </div>
        {showStar && (
          <span
            className="inline-flex items-center gap-1.5"
            title={`Recommandation appuyée sur un signal contextuel · ${satelliteSignal.title || ''}`}
            data-testid={`priority-star-${rank}`}
          >
            <Star size={13} className="text-cabinet-gold" fill="#B5862A" strokeWidth={1.5} />
            <span className="text-[9px] text-cabinet-gold uppercase tracking-[0.22em] font-medium">
              Signal contextuel
            </span>
          </span>
        )}
      </header>

      <div className="p-7 space-y-6">
        <h3 className="font-manrope font-medium text-[16px] text-cabinet-ink leading-snug">
          {priority?.title || 'Priorité du mois'}
        </h3>

        {priority?.context_question && (
          <div className="bg-cabinet-cream border-l-2 border-l-cabinet-gold-soft px-5 py-3" data-testid={`priority-context-${rank}`}>
            <div className="text-[9px] text-cabinet-bordeaux uppercase tracking-[0.22em] font-medium mb-1.5">
              Question de contexte que nous nous sommes posée
            </div>
            <p className="font-playfair italic text-[13px] text-cabinet-ink-secondary leading-[1.7]">
              {priority.context_question}
            </p>
          </div>
        )}

        {analysis && (
          <MarkdownText
            text={analysis}
            className="text-[14px] text-cabinet-ink leading-[1.75]"
          />
        )}

        {steps.length > 0 && (
          <div className="bg-cabinet-cream-dark border-l-2 border-l-cabinet-bordeaux px-5 py-4" data-testid={`priority-steps-${rank}`}>
            <div className="text-[9px] text-cabinet-bordeaux uppercase tracking-[0.22em] font-medium mb-3">
              Plan d'action concret · {monthLabel || periodReadable}
            </div>
            <ul className="space-y-2.5">
              {steps.slice(0, 5).map((step, i) => {
                const stepText = typeof step === 'string' ? step : step?.text || '';
                const stepDate = typeof step === 'object' ? step?.date : null;
                return (
                  <li key={i} className="flex gap-3 text-[13px] text-cabinet-ink leading-[1.7]">
                    <span className="text-cabinet-bordeaux mt-0.5 select-none">—</span>
                    <span className="flex-1">
                      {stepDate && (
                        <span className="text-cabinet-bordeaux font-medium uppercase text-[10px] tracking-[0.18em] mr-2">
                          {formatStepDate(stepDate)} ·
                        </span>
                      )}
                      {stepText}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <footer className="bg-cabinet-cream-dark border-t border-cabinet-border px-7 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.18em] mb-1">
            Impact estimé
          </div>
          <div className="font-playfair text-[15px] text-cabinet-forest font-medium">
            {priority?.impact_eur_annual != null
              ? `${formatCurrencyFr(priority.impact_eur_annual)} / an`
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.18em] mb-1">
            Difficulté
          </div>
          <div className="font-manrope text-[13px] text-cabinet-ink capitalize">
            {priority?.difficulty || '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.18em] mb-1">
            Délai
          </div>
          <div className="font-manrope text-[13px] text-cabinet-ink">
            {priority?.delay || '—'}
          </div>
        </div>
      </footer>
    </article>
  );
}
